import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  DatePicker,
  Select,
  InputNumber,
  Table,
  Tag,
  message,
  Spin,
  Divider,
  Modal,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  MinusCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
  CheckOutlined,
  RollbackOutlined,
  CarOutlined,
  InboxOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import stockTransferService from '@/services/stockTransfer.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { ENTITY_TYPE_STOCK_TRANSFER } from '@/constants/approval-entity-types';
import { useWarehouses, useItems } from '@/hooks/useInventory';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useApplyUserDefaultDepartment } from '@/utils/userDefaultDepartment';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { useAuthStore } from '@/store/authStore';
import { isPlatformSuperAdmin } from '@/utils/authUser';

const { Title, Text } = Typography;
const { TextArea } = Input;

const StockTransferDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  useApplyUserDefaultDepartment(form, isNew);
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const superAdmin = isPlatformSuperAdmin(user);
  const perms = user?.permissions ?? [];
  const has = (p: string) => superAdmin || perms.includes(p);
  const { isCriticalCode } = useCriticalItemLookup();
  const { data: items = [] } = useItems({ is_active: true });
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const { data: warehouses = [] } = useWarehouses();

  const { data: transfer, isLoading } = useQuery({
    queryKey: ['stock-transfer', id],
    queryFn: () => stockTransferService.getById(id!),
    enabled: !isNew && !!id,
  });

  const fromWhId = Form.useWatch('from_warehouse_id', form);
  const toWhId = Form.useWatch('to_warehouse_id', form);
  const transferType = Form.useWatch('transfer_type', form) || 'Warehouse to Warehouse';
  const isP2P = transferType === 'Project to Project';

  const lockedProjectId = useMemo(() => {
    if (isP2P) return null;
    const from = warehouses.find((w) => w.id === fromWhId);
    const to = warehouses.find((w) => w.id === toWhId);
    return from?.project_id || to?.project_id || null;
  }, [warehouses, fromWhId, toWhId, isP2P]);

  useEffect(() => {
    if (!isP2P) return;
    const from = warehouses.find((w) => w.id === fromWhId);
    const to = warehouses.find((w) => w.id === toWhId);
    form.setFieldsValue({
      from_project_id: from?.project_id || undefined,
      to_project_id: to?.project_id || undefined,
    });
  }, [isP2P, fromWhId, toWhId, warehouses, form]);

  useEffect(() => {
    if (lockedProjectId && !isP2P) {
      form.setFieldsValue({ project_id: lockedProjectId });
    }
  }, [lockedProjectId, form, isP2P]);

  useEffect(() => {
    if (!transfer || isNew) return;
    form.setFieldsValue({
      transfer_date: transfer.transfer_date ? dayjs(transfer.transfer_date) : dayjs(),
      transfer_type: transfer.transfer_type || 'Warehouse to Warehouse',
      from_warehouse_id: transfer.from_warehouse_id,
      to_warehouse_id: transfer.to_warehouse_id,
      project_id: transfer.project_id,
      from_project_id: transfer.from_project_id,
      to_project_id: transfer.to_project_id,
      department_id: transfer.department_id || undefined,
      reason: transfer.reason,
      notes: transfer.notes,
      items: (transfer.items || []).map((l) => ({
        key: l.id,
        item_id: l.item_id,
        transfer_quantity: Number(l.transfer_quantity),
        uom: l.uom,
        unit_cost: Number(l.unit_cost),
        heat_number: l.heat_number,
        dimension: l.dimension,
        notes: l.notes,
      })),
    });
  }, [transfer, isNew, form]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
    queryClient.invalidateQueries({ queryKey: ['stock-transfer', id] });
  };

  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const payload = {
        transfer_date: (values.transfer_date as dayjs.Dayjs).format('YYYY-MM-DD'),
        from_warehouse_id: values.from_warehouse_id as string,
        to_warehouse_id: values.to_warehouse_id as string,
        transfer_type: (values.transfer_type as string) || 'Warehouse to Warehouse',
        project_id: (values.project_id as string) || lockedProjectId || null,
        from_project_id: (values.from_project_id as string) || null,
        to_project_id: (values.to_project_id as string) || null,
        department_id: values.department_id as string,
        reason: (values.reason as string) || null,
        notes: (values.notes as string) || null,
        items: ((values.items as Array<Record<string, unknown>>) || []).map((row) => ({
          item_id: row.item_id as string,
          transfer_quantity: Number(row.transfer_quantity),
          uom: row.uom as string | undefined,
          unit_cost: row.unit_cost != null ? Number(row.unit_cost) : undefined,
          heat_number: (row.heat_number as string) || null,
          dimension: (row.dimension as string) || null,
          notes: (row.notes as string) || null,
        })),
      };
      if (isNew) return stockTransferService.create(payload);
      return stockTransferService.update(id!, payload);
    },
    onSuccess: (row) => {
      message.success('Saved');
      invalidate();
      if (isNew) navigate(`/scm/warehouse/stock-transfer/${row.id}`, { replace: true });
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      message.error(e.response?.data?.message || 'Save failed');
    },
  });

  const runAction = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      message.success(ok);
      invalidate();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || 'Action failed');
    }
  };

  const status = transfer?.transfer_status || 'Draft';
  const editable = isNew || status === 'Draft';

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveQtys, setReceiveQtys] = useState<Record<string, number>>({});

  const openReceive = () => {
    const map: Record<string, number> = {};
    for (const l of transfer?.items || []) {
      const rem = Number(l.shipped_quantity || 0) - Number(l.received_quantity || 0);
      if (rem > 0) map[l.id] = rem;
    }
    setReceiveQtys(map);
    setReceiveOpen(true);
  };

  const printSimple = () => {
    if (!transfer) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const from = warehouses.find((x) => x.id === transfer.from_warehouse_id);
    const to = warehouses.find((x) => x.id === transfer.to_warehouse_id);
    const lines = (transfer.items || [])
      .map(
        (l) =>
          `<tr><td>${l.item_code || ''}</td><td>${l.item_description || ''}</td><td>${l.transfer_quantity}</td><td>${l.shipped_quantity}</td><td>${l.received_quantity}</td><td>${l.uom || ''}</td><td>${l.heat_number || ''}</td><td>${l.dimension || ''}</td></tr>`
      )
      .join('');
    w.document.write(`<!DOCTYPE html><html><head><title>${transfer.transfer_number}</title>
      <style>body{font-family:sans-serif;padding:24px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #ccc;padding:6px;font-size:12px}</style>
      </head><body>
      <h2>Stock Transfer ${transfer.transfer_number}</h2>
      <p>Status: ${transfer.transfer_status} | Date: ${transfer.transfer_date}</p>
      <p>From: ${from?.warehouse_name || transfer.from_warehouse_id}<br/>To: ${to?.warehouse_name || transfer.to_warehouse_id}</p>
      <table><thead><tr><th>Code</th><th>Name</th><th>Qty</th><th>Shipped</th><th>Received</th><th>UOM</th><th>Heat</th><th>Dim</th></tr></thead>
      <tbody>${lines}</tbody></table>
      <script>window.print()</script></body></html>`);
    w.document.close();
  };

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button onClick={() => navigate('/scm/warehouse/stock-transfer')}>Back</Button>
        {!isNew && transfer ? (
          <>
            <Button icon={<PrinterOutlined />} onClick={printSimple}>
              Print
            </Button>
            <ReportDocumentActions
              reportKey="stock-transfer"
              params={{ id: transfer.id }}
              documentId={transfer.id}
              sendPermissions={['inventory.transfer.edit', 'inventory.transfer.create']}
            />
          </>
        ) : null}
      </Space>

      <Title level={2} style={{ marginTop: 0 }}>
        {isNew ? 'New Stock Transfer' : transfer?.transfer_number}
      </Title>
      {!isNew && transfer ? (
        <Space style={{ marginBottom: 16 }} wrap>
          <Tag>{transfer.transfer_status}</Tag>
          <Tag color="blue">{transfer.approval_status}</Tag>
          {transfer.ship_journal_voucher_id ? (
            <Tag color="geekblue">
              Ship JV:{' '}
              <a href={`/fin/gl/journals?vid=${transfer.ship_journal_voucher_id}`}>
                {transfer.ship_journal_voucher_id.slice(0, 8)}…
              </a>
            </Tag>
          ) : null}
          {transfer.receive_journal_voucher_id ? (
            <Tag color="geekblue">
              Receive JV:{' '}
              <a href={`/fin/gl/journals?vid=${transfer.receive_journal_voucher_id}`}>
                {transfer.receive_journal_voucher_id.slice(0, 8)}…
              </a>
            </Tag>
          ) : null}
        </Space>
      ) : null}

      {!isNew && transfer?.approval_instance_id ? (
        <div style={{ marginBottom: 16 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_STOCK_TRANSFER}
            entityId={transfer.id}
            onApproveViaDocument={async (remarks) => {
              await stockTransferService.approve(transfer.id, remarks);
              invalidate();
            }}
            onRejectViaDocument={async (payload) => {
              await stockTransferService.rejectApproval(transfer.id, payload);
              invalidate();
            }}
            extraInvalidateQueryKeys={[['stock-transfers'], ['stock-transfer', transfer.id]]}
          />
        </div>
      ) : null}

      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            transfer_date: dayjs(),
            transfer_type: 'Warehouse to Warehouse',
            items: [{ transfer_quantity: 1 }],
          }}
          onFinish={(v) => saveMutation.mutate(v)}
          disabled={!editable}
        >
          <Space wrap size="large" style={{ width: '100%' }}>
            <Form.Item
              name="transfer_type"
              label="Transfer type"
              rules={[{ required: true }]}
              style={{ minWidth: 220 }}
            >
              <Select
                options={[
                  { value: 'Warehouse to Warehouse', label: 'Warehouse to Warehouse' },
                  { value: 'Project to Project', label: 'Project to Project (WIP ↔ WIP)' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="transfer_date"
              label="Transfer date"
              rules={[{ required: true }]}
              style={{ minWidth: 180 }}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: 'Department is required' }]}
              style={{ minWidth: 280 }}
            >
              <DepartmentSelect />
            </Form.Item>
            <Form.Item
              name="from_warehouse_id"
              label="From warehouse"
              rules={[{ required: true }]}
              style={{ minWidth: 220 }}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={warehouses.map((w) => ({
                  value: w.id,
                  label: `${w.warehouse_name}${w.project_id ? ' (WIP)' : ''}`,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="to_warehouse_id"
              label="To warehouse"
              rules={[{ required: true }]}
              style={{ minWidth: 220 }}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={warehouses.map((w) => ({
                  value: w.id,
                  label: `${w.warehouse_name}${w.project_id ? ' (WIP)' : ''}`,
                }))}
              />
            </Form.Item>
          </Space>
          {isP2P ? (
            <Space wrap size="large" style={{ width: '100%', marginTop: 8 }}>
              <Form.Item
                name="from_project_id"
                label="From project"
                rules={[{ required: true, message: 'Required for P2P' }]}
                style={{ minWidth: 260 }}
              >
                <Input placeholder="Source project id (or pick via WIP warehouse)" />
              </Form.Item>
              <Form.Item
                name="to_project_id"
                label="To project"
                rules={[{ required: true, message: 'Required for P2P' }]}
                style={{ minWidth: 260 }}
              >
                <Input placeholder="Destination project id" />
              </Form.Item>
            </Space>
          ) : lockedProjectId ? (
            <Text type="secondary">Project locked from WIP warehouse: {lockedProjectId.slice(0, 8)}…</Text>
          ) : null}
          <Form.Item name="project_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} />
          </Form.Item>

          <Divider>Lines</Divider>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                <Table
                  pagination={false}
                  size="small"
                  dataSource={fields}
                  rowKey="key"
                  rowClassName={(field) => {
                    const itemId = form.getFieldValue(['items', field.name, 'item_id']);
                    return getCriticalItemRowClassName({
                      is_critical: isItemCritical(itemsById.get(itemId)),
                    });
                  }}
                  columns={[
                    {
                      title: 'Item',
                      render: (_, field) => (
                        <Form.Item noStyle shouldUpdate>
                          {() => {
                            const itemId = form.getFieldValue(['items', field.name, 'item_id']);
                            const critical = isItemCritical(itemsById.get(itemId));
                            return (
                              <Space size={6} wrap>
                                <Form.Item
                                  {...field}
                                  name={[field.name, 'item_id']}
                                  rules={[{ required: true, message: 'Required' }]}
                                  style={{ margin: 0 }}
                                >
                                  <ItemSelect items={items} autoLoad={false} style={{ minWidth: 220 }} />
                                </Form.Item>
                                <CriticalItemTag critical={critical} />
                              </Space>
                            );
                          }}
                        </Form.Item>
                      ),
                    },
                    {
                      title: 'Qty',
                      width: 110,
                      render: (_, field) => (
                        <Form.Item
                          {...field}
                          name={[field.name, 'transfer_quantity']}
                          rules={[{ required: true }]}
                          style={{ margin: 0 }}
                        >
                          <InputNumber min={0.0001} style={{ width: '100%' }} />
                        </Form.Item>
                      ),
                    },
                    {
                      title: 'UOM',
                      width: 90,
                      render: (_, field) => (
                        <Form.Item {...field} name={[field.name, 'uom']} style={{ margin: 0 }}>
                          <Input />
                        </Form.Item>
                      ),
                    },
                    {
                      title: 'Heat',
                      width: 110,
                      render: (_, field) => (
                        <Form.Item {...field} name={[field.name, 'heat_number']} style={{ margin: 0 }}>
                          <Input />
                        </Form.Item>
                      ),
                    },
                    {
                      title: 'Dim',
                      width: 110,
                      render: (_, field) => (
                        <Form.Item {...field} name={[field.name, 'dimension']} style={{ margin: 0 }}>
                          <Input />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '',
                      width: 48,
                      render: (_, field) =>
                        editable ? (
                          <Button
                            type="text"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                          />
                        ) : null,
                    },
                  ]}
                />
                {editable ? (
                  <Button
                    type="dashed"
                    onClick={() => add({ transfer_quantity: 1 })}
                    block
                    icon={<PlusOutlined />}
                    style={{ marginTop: 8 }}
                  >
                    Add line
                  </Button>
                ) : null}
              </>
            )}
          </Form.List>

          {editable ? (
            <Space style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={saveMutation.isPending}
              >
                Save
              </Button>
            </Space>
          ) : null}
        </Form>
      </Card>

      {!isNew && transfer ? (
        <Card title="Actions" style={{ marginTop: 16 }}>
          <Space wrap>
            {status === 'Draft' && has('inventory.transfer.submit') ? (
              <Button
                icon={<SendOutlined />}
                onClick={() => runAction(() => stockTransferService.submit(transfer.id), 'Submitted')}
              >
                Submit for approval
              </Button>
            ) : null}
            {(status === 'Draft' || status === 'Pending Approval') &&
            has('inventory.transfer.approve') &&
            !transfer.approval_instance_id ? (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => runAction(() => stockTransferService.approve(transfer.id), 'Approved')}
              >
                Approve
              </Button>
            ) : null}
            {status === 'Approved' && has('inventory.transfer.ship') ? (
              <Button
                type="primary"
                icon={<CarOutlined />}
                onClick={() => runAction(() => stockTransferService.ship(transfer.id), 'Shipped')}
              >
                Ship
              </Button>
            ) : null}
            {status === 'In Transit' && has('inventory.transfer.receive') ? (
              <Button type="primary" icon={<InboxOutlined />} onClick={openReceive}>
                Receive
              </Button>
            ) : null}
            {['Draft', 'Pending Approval', 'Approved', 'In Transit'].includes(status) &&
            has('inventory.transfer.cancel') ? (
              <Button
                danger
                icon={<RollbackOutlined />}
                onClick={() =>
                  Modal.confirm({
                    title: 'Cancel transfer?',
                    content:
                      status === 'In Transit'
                        ? 'Unreceived shipped quantity will be returned to the source warehouse.'
                        : undefined,
                    onOk: () => runAction(() => stockTransferService.cancel(transfer.id), 'Cancelled'),
                  })
                }
              >
                Cancel
              </Button>
            ) : null}
          </Space>

          {transfer.items?.length ? (
            <Table
              style={{ marginTop: 16 }}
              size="small"
              pagination={false}
              rowKey="id"
              dataSource={transfer.items}
              rowClassName={(l) =>
                getCriticalItemRowClassName({ is_critical: isCriticalCode(l.item_code) })
              }
              columns={[
                {
                  title: 'Item',
                  dataIndex: 'item_code',
                  render: (code: string, l) => (
                    <Space size={6} wrap>
                      <span>{code}</span>
                      <CriticalItemTag critical={isCriticalCode(l.item_code)} />
                    </Space>
                  ),
                },
                { title: 'Name', dataIndex: 'item_description', ellipsis: true },
                { title: 'Transfer qty', dataIndex: 'transfer_quantity' },
                { title: 'Shipped', dataIndex: 'shipped_quantity' },
                { title: 'Received', dataIndex: 'received_quantity' },
                { title: 'Heat', dataIndex: 'heat_number', render: (v) => v || '—' },
                { title: 'Dim', dataIndex: 'dimension', render: (v) => v || '—' },
              ]}
            />
          ) : null}
        </Card>
      ) : null}

      <Modal
        title="Receive transfer"
        open={receiveOpen}
        onCancel={() => setReceiveOpen(false)}
        onOk={() => {
          const lines = Object.entries(receiveQtys)
            .filter(([, q]) => Number(q) > 0)
            .map(([line_id, receive_quantity]) => ({ line_id, receive_quantity: Number(receive_quantity) }));
          setReceiveOpen(false);
          runAction(() => stockTransferService.receive(transfer!.id, lines), 'Received');
        }}
      >
        {(transfer?.items || [])
          .filter((l) => Number(l.shipped_quantity) - Number(l.received_quantity) > 0)
          .map((l) => {
            const rem = Number(l.shipped_quantity) - Number(l.received_quantity);
            return (
              <div key={l.id} style={{ marginBottom: 12 }}>
                <Text>
                  {l.item_code} — remaining {rem}
                </Text>
                <InputNumber
                  style={{ width: '100%', marginTop: 4 }}
                  min={0}
                  max={rem}
                  value={receiveQtys[l.id]}
                  onChange={(v) => setReceiveQtys((prev) => ({ ...prev, [l.id]: Number(v || 0) }))}
                />
              </div>
            );
          })}
      </Modal>
    </div>
  );
};

export default StockTransferDetailPage;
